import React, { memo, FC, useMemo } from 'react';
import classNames from 'classnames';
import {
  makeStyles,
  shorthands,
  TableCellLayout,
  Text,
} from '@fluentui/react-components';

import { IColumnData, IMetricFormatter, TMajorMetric } from '../types';

export const useStyles = makeStyles({
  metricCell: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  metricCellContent: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  metricCellComparison: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
});

const comparisonValuePositionMap = {
  PerformanceMetrics: {
    value: 'value',
    sup: 'changePercentValue',
    sub: 'changeValue',
  },
  PerformanceMetricsChangePercentage: {
    value: 'changePercentValue',
    sup: 'value',
    sub: 'changeValue',
  },
  PerformanceMetricsChange: {
    value: 'changeValue',
    sup: 'value',
    sub: 'changePercentValue',
  },
};

export interface IMetricCellProps {
  columnData: IColumnData;
  metricFormatter: IMetricFormatter;
  comparisonMajorMetric: TMajorMetric;
}

export const MetricCell: FC<IMetricCellProps> = memo(({
  columnData,
  metricFormatter,
  comparisonMajorMetric,
}: IMetricCellProps) => {
  const classes = useStyles();
  const {
    formatMetric,
    formatMetricWithSign,
    formatChangePercentageWithSign,
  } = metricFormatter;
  const {
    columnConfig: {
      columnId,
    },
  } = columnData;
  const shouldShowComparison = useMemo(() => columnData.columnConfig.isSorted && columnData.PerformanceMetricsChange && columnData.PerformanceMetricsChangePercentage, [columnData]);
  const values = useMemo(() => {
    const value = formatMetric(columnId, columnData.PerformanceMetrics[columnId], {
      currency: columnData.Currency,
    });
    if (!shouldShowComparison) {
      return {
        value,
      };
    }

    const changePercentValue = formatChangePercentageWithSign(columnData.PerformanceMetricsChangePercentage[columnId]);
    const changeValue = formatMetricWithSign(columnId, columnData.PerformanceMetricsChange[columnId]);

    return {
      value,
      changePercentValue,
      changeValue,
    };
  }, [columnData.PerformanceMetrics, columnData.PerformanceMetricsChange, columnData.PerformanceMetricsChangePercentage, columnId, formatChangePercentageWithSign, formatMetric, formatMetricWithSign, shouldShowComparison]);

  const positionMap = comparisonValuePositionMap[comparisonMajorMetric];
  const comparisonContent = useMemo(() => {
    if (!shouldShowComparison) {
      return null;
    }

    const supValue = values[positionMap.sup];
    const subValue = values[positionMap.sub];

    return (
      <div className={classNames('ba-performance-table-metric-cell-comparison', classes.metricCellComparison)}>
        <Text className="ba-performance-table-metric-cell-sup" size={100} >{supValue}</Text>
        <Text className="ba-performance-table-metric-cell-sub" size={100} >{subValue}</Text>
      </div>
    );
  }, [classes, positionMap, shouldShowComparison, values]);

  const major = shouldShowComparison ? values[positionMap.value] : values.value;

  return (
    <TableCellLayout truncate className={classNames('ba-performance-table-metric-cell', classes.metricCell)} >
      <div className={classNames('ba-performance-table-metric-cell-content', classes.metricCellContent)}>
        <Text className="ba-performance-table-metric-cell-value" size={200} >{major}</Text>
        {comparisonContent}
      </div>
    </TableCellLayout>
  );
});

MetricCell.defaultProps = {
};
